/**
 * Terms, privacy, refunds and contact.
 *
 * Written to be read, and written to be true. Two rules held throughout:
 *
 * **Nothing here describes behaviour the product does not have.** The refund
 * policy matches `cancelSubscription` in core/subscription.ts — a cancellation
 * keeps the plan until the end of the period already paid for, because that is
 * what the code does. The privacy page says the questionnaire is parsed in the
 * browser because src/app/sheet.ts parses it there. A legal page is the one part
 * of a product that gets quoted back at you, and this one is sold on refusing to
 * claim what it cannot back up.
 *
 * **The seller is named or the page says it is not.** Until src/core/company.ts
 * has a legal entity, these pages carry a visible notice instead of a plausible
 * placeholder. An unfinished document that admits it is unfinished can be
 * published; one that names an entity which does not exist cannot.
 */

import type { ReactNode } from 'react';

import {
  COMPANY,
  LEGAL_EFFECTIVE_DATE,
  MERCHANT_OF_RECORD,
  PRIVACY_EMAIL,
  SECURITY_EMAIL,
  SUBPROCESSORS,
  SUPPORT_EMAIL,
  sellerName,
} from '../../core/company';
import { PLANS } from '../../core/plans';
import { link } from '../router';
import { SiteFooter, SiteHeader } from './Chrome';

/** Prose styling in one place, so four documents cannot drift apart visually. */
const PROSE = [
  'space-y-4 text-[14px] leading-relaxed text-muted',
  '[&_h2]:mt-10 [&_h2]:mb-3 [&_h2]:text-[17px] [&_h2]:font-medium [&_h2]:text-ink',
  '[&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:text-[14.5px] [&_h3]:font-medium [&_h3]:text-ink',
  '[&_strong]:font-medium [&_strong]:text-ink',
  '[&_ul]:list-disc [&_ul]:space-y-2 [&_ul]:pl-5 [&_li]:marker:text-faint',
  '[&_a]:text-accent [&_a]:underline [&_a]:underline-offset-2',
].join(' ');

function Mail({ address }: { address: string }) {
  return <a href={`mailto:${address}`}>{address}</a>;
}

function LegalPage({
  title,
  summary,
  children,
  signedIn,
}: {
  title: string;
  /** One sentence, above the fold. Most people read this and nothing else. */
  summary: string;
  children: ReactNode;
  signedIn?: boolean;
}) {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader signedIn={signedIn} />

      <main className="mx-auto w-full max-w-3xl flex-1 px-5 py-14">
        <h1 className="text-[32px] leading-tight">{title}</h1>
        <p className="mt-3 text-[15px] leading-relaxed text-muted">{summary}</p>
        <p className="mt-4 text-[12.5px] text-faint">
          Effective {LEGAL_EFFECTIVE_DATE}. We keep the previous version on request.
        </p>

        <div className={`mt-10 ${PROSE}`}>{children}</div>

        <p className="mt-12 border-t border-line pt-6 text-[12.5px] leading-relaxed text-faint">
          Questions about this page go to <Mail address={SUPPORT_EMAIL} />, and we answer within two
          working days. See also <a {...link('/terms')}>the terms</a>,{' '}
          <a {...link('/privacy')}>the privacy policy</a>, and{' '}
          <a {...link('/refunds')}>refunds and cancellation</a>.
        </p>
      </main>

      <SiteFooter />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* terms                                                                      */
/* -------------------------------------------------------------------------- */

export function TermsView({ signedIn }: { signedIn?: boolean }) {
  return (
    <LegalPage
      signedIn={signedIn}
      title="Terms of service"
      summary={`The agreement between you and ${sellerName()} for the use of ${COMPANY.product}. Plain language, and short enough to actually read before you agree to it.`}
    >
      <h2>1. Who you are dealing with</h2>
      <p>
        {COMPANY.product} is operated by <strong>{sellerName()}</strong>
        {COMPANY.jurisdiction ? `, established in ${COMPANY.jurisdiction}` : ''}
        {COMPANY.registrationNumber ? ` (registration ${COMPANY.registrationNumber})` : ''}. In
        these terms "we" and "us" mean {sellerName()}, and "you" means the company or person using
        the service.
      </p>
      <p>
        Payment is taken by {MERCHANT_OF_RECORD.name} as our {MERCHANT_OF_RECORD.role}. That makes
        Polar the seller on the transaction and the party responsible for your invoice and for any
        sales tax or VAT. Their terms cover the payment itself; these terms cover the service.
      </p>

      <h2>2. What the service does</h2>
      <p>
        {COMPANY.product} keeps a bank of evidence about your security practices, drafts answers to
        security questionnaires that cite that evidence, and publishes a trust page. Every answer it
        produces is either supported by a citation to a current piece of your evidence or is returned
        marked as unsupported with the draft text discarded.
      </p>

      <h3>What it does not do, stated plainly</h3>
      <ul>
        <li>
          <strong>It does not certify anything.</strong> {COMPANY.product} is not an audit, not a
          compliance programme, and not a substitute for SOC 2, ISO 27001 or any other framework. It
          does not assess whether your practices are adequate — only whether an answer you are about
          to send is backed by something you wrote down.
        </li>
        <li>
          <strong>It does not give legal advice.</strong> What you send to a customer's security team
          is a statement by your company, and the responsibility for its accuracy stays with you.
          The citation requirement exists to make that responsibility easier to discharge, not to
          transfer it to us.
        </li>
        <li>
          <strong>It does not verify your evidence.</strong> An evidence card says what you typed
          into it. We check that a card exists and that a human reviewed it recently enough; we do
          not check that it is true.
        </li>
      </ul>
      <p>
        You are responsible for reviewing every answer before it leaves your hands. The product is
        built to make an unsupported answer difficult to send by accident; it cannot make a
        supported answer correct.
      </p>

      <h2>3. Your account</h2>
      <ul>
        <li>You need an accurate email address, because it is how you get back in.</li>
        <li>
          You are responsible for what happens under your account and for keeping your password to
          yourself. Tell us at <Mail address={SECURITY_EMAIL} /> if you think it has been used by
          somebody else.
        </li>
        <li>
          One account represents one company today. There are no separate seats yet, and we do not
          pretend otherwise on the pricing page.
        </li>
      </ul>

      <h2>4. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>
          upload documents or evidence you do not have the right to share, including another
          company's confidential material;
        </li>
        <li>
          use the service to make statements about a company you are not authorised to speak for;
        </li>
        <li>
          attempt to reach another account's data, or to probe, scan or overload the service — with
          the exception of good-faith security research reported to <Mail address={SECURITY_EMAIL} />
          , which we welcome;
        </li>
        <li>resell or rebrand the service as your own.</li>
      </ul>

      <h2>5. Your content stays yours</h2>
      <p>
        Your evidence cards, documents, questionnaires and answers belong to you. You grant us only
        the permission needed to run the service for you: to store that material, to display it back
        to you, and — where you have explicitly published it — to serve it on your public trust page.
        We do not use it to train models, and we do not share it with anyone outside the
        subprocessors listed in <a {...link('/privacy')}>the privacy policy</a>.
      </p>
      <p>
        You can export your answers and delete your account at any time. Deleting an account removes
        your evidence, documents and answer history; see the privacy policy for the timing.
      </p>

      <h2>6. Your model key</h2>
      <p>
        Drafting runs on an API key you supply for your own model provider. The key travels with the
        request that uses it and is never written to our database. Two things follow, and both are
        yours rather than ours: the tokens your drafting consumes are billed to you by your provider,
        and your provider's terms apply to the question text sent through it.
      </p>

      <h2>7. Plans and payment</h2>
      <p>
        Paid plans are billed monthly in advance at the prices shown on{' '}
        <a {...link('/pricing')}>the pricing page</a> — {PLANS.team.name} at ${PLANS.team.price} a
        month and {PLANS.business.name} at ${PLANS.business.price} a month, in {PLANS.team.currency}.
        A plan renews on its own until you cancel it.
      </p>
      <p>
        The Free plan is free, and it stays free. It is limited rather than time-limited: there is no
        trial that quietly expires into a bill.
      </p>
      <p>
        If we change a price, we will tell you by email at least 30 days before it applies to your
        account, and you can cancel before it does. Cancellation and refunds are covered in{' '}
        <a {...link('/refunds')}>refunds and cancellation</a>, which forms part of these terms.
      </p>

      <h2>8. Availability</h2>
      <p>
        We do not offer a contractual uptime guarantee, and we would rather say so than print a
        number we have no mechanism to honour. What we do commit to: planned maintenance that will
        take the service down is announced by email in advance, and unplanned downtime is worked on
        immediately.
      </p>
      <p>
        Your public trust page is served from the same infrastructure as the app, so it goes down
        when the app does. If that page is load-bearing in a live procurement conversation, keep the
        documents you would need to send by hand.
      </p>

      <h2>9. Suspension and termination</h2>
      <p>
        You can stop at any time by cancelling and, if you want, deleting your account. We may
        suspend an account that breaks section 4, that is being used to attack the service, or whose
        payment has failed and stayed failed. Except where the law requires otherwise or an account
        is actively causing harm, we will email you first and give you a chance to fix it.
      </p>
      <p>
        If we terminate an account that has not broken these terms, we refund the unused part of the
        period you have paid for.
      </p>

      <h2>10. Warranties and liability</h2>
      <p>
        The service is provided as it is. We do not warrant that it will be uninterrupted, that a
        draft will be useful, or that a questionnaire will close a deal.
      </p>
      <p>
        To the extent the law allows, our total liability to you for any claim arising out of these
        terms is limited to the fees you paid us in the twelve months before the claim, and we are
        not liable for lost profits, lost deals or indirect losses. Nothing here limits liability
        for fraud, for death or personal injury caused by negligence, or for anything else that
        cannot lawfully be limited — including your rights as a consumer, if you are one.
      </p>

      <h2>11. Changes</h2>
      <p>
        We may change these terms. Material changes are announced by email at least 30 days before
        they take effect, and the effective date at the top of this page always tells you which
        version you are looking at. Continuing to use the service after that date means the new terms
        apply; if they do not suit you, cancel before it and the refund policy applies as normal.
      </p>

      <h2>12. Governing law and disputes</h2>
      {COMPANY.jurisdiction ? (
        <p>
          These terms are governed by the law of {COMPANY.jurisdiction}, and its courts have
          jurisdiction over any dispute — without affecting any protection you have under the
          mandatory law of the country you live in.
        </p>
      ) : null}
      <p>
        Before anyone involves a court, write to <Mail address={SUPPORT_EMAIL} />. Most things that
        look like a dispute are a misunderstanding about a renewal date.
      </p>
    </LegalPage>
  );
}

/* -------------------------------------------------------------------------- */
/* privacy                                                                    */
/* -------------------------------------------------------------------------- */

export function PrivacyView({ signedIn }: { signedIn?: boolean }) {
  return (
    <LegalPage
      signedIn={signedIn}
      title="Privacy policy"
      summary={`What ${COMPANY.product} receives, what it deliberately does not receive, and who else ever sees it. The short version: your internal documentation is the product's whole subject, so the design keeps as much of it away from us as it can.`}
    >
      <h2>1. Who is responsible</h2>
      <p>
        <strong>{sellerName()}</strong>
        {COMPANY.jurisdiction ? `, established in ${COMPANY.jurisdiction},` : ''} is the controller
        of the personal data described here. For questions or requests, write to{' '}
        <Mail address={PRIVACY_EMAIL} />.
      </p>
      <p>
        Where you upload evidence and documents about your own company, we act as a processor on your
        instructions: the material is yours, we hold it to run the service, and we do nothing else
        with it.
      </p>

      <h2>2. What we hold</h2>
      <ul>
        <li>
          <strong>Account data</strong> — your email address, a hashed password, your company name,
          and the address of your trust page.
        </li>
        <li>
          <strong>Evidence cards</strong> — the claims you write down about your security practices,
          their sources, and the date each was last reviewed.
        </li>
        <li>
          <strong>Question text</strong> — the questions extracted from a questionnaire you import,
          and the answers settled against them.
        </li>
        <li>
          <strong>Documents</strong> — files you choose to attach to your trust page, along with a
          log of who requested access to them and when, which is a feature you asked for and can see
          in full.
        </li>
        <li>
          <strong>Billing data</strong> — held by Polar, not by us. We store your Polar customer
          identifier, your plan and its renewal date. We never see your card.
        </li>
        <li>
          <strong>Operational logs</strong> — errors and request metadata, retained briefly for
          debugging. Client IP addresses are used for rate limiting as a salted hash and are not
          stored in the clear.
        </li>
      </ul>

      <h2>3. What we never receive</h2>
      <p>This list is a design decision, and it is the reason several features work the way they do.</p>
      <ul>
        <li>
          <strong>The spreadsheet itself.</strong> A questionnaire is parsed in your browser. Only
          the question text is sent to us, and only after you have seen which column it came from.
          The file never leaves your machine.
        </li>
        <li>
          <strong>Your model API key at rest.</strong> It is sent with the request that uses it,
          used once, and never written down. We have no standing credential of yours to lose.
        </li>
        <li>
          <strong>Your card number.</strong> It goes to Polar and their payment processors. There is
          no code in this application that could receive one.
        </li>
        <li>
          <strong>Anything for advertising.</strong> There are no analytics scripts, no tracking
          pixels and no third-party tags on any page — the content security policy blocks external
          origins outright, so there is nowhere for one to phone home to.
        </li>
      </ul>

      <h2>4. Why we hold it, and on what basis</h2>
      <ul>
        <li>
          <strong>To provide the service</strong> — performance of our contract with you. This covers
          your account, evidence, questionnaires and trust page.
        </li>
        <li>
          <strong>To take payment</strong> — performance of the contract, and a legal obligation for
          the invoice and tax records Polar keeps.
        </li>
        <li>
          <strong>To email you about your account</strong> — password resets, receipts and the
          evidence review reminder. Legitimate interest, and the reminder is the only one you can
          turn off.
        </li>
        <li>
          <strong>To keep the service up and safe</strong> — legitimate interest in rate limiting,
          abuse prevention and debugging.
        </li>
      </ul>
      <p>We do not sell personal data, and we do not use your content to train any model.</p>

      <h2>5. Who else sees it</h2>
      <p>
        Only these, and the list is short on purpose — every addition weakens the promise in section
        3.
      </p>
      <div className="not-prose overflow-x-auto">
        <table className="w-full min-w-[36rem] border-collapse text-left text-[13px]">
          <thead>
            <tr className="border-b border-line text-[12px] uppercase tracking-wide text-faint">
              <th className="py-2 pr-4 font-medium">Who</th>
              <th className="py-2 pr-4 font-medium">What for</th>
              <th className="py-2 pr-4 font-medium">What they get</th>
              <th className="py-2 font-medium">Where</th>
            </tr>
          </thead>
          <tbody>
            {SUBPROCESSORS.map((sub) => (
              <tr key={sub.name} className="border-b border-line align-top">
                <td className="py-3 pr-4 text-ink">{sub.name}</td>
                <td className="py-3 pr-4 text-muted">{sub.purpose}</td>
                <td className="py-3 pr-4 text-muted">{sub.dataReceived}</td>
                <td className="py-3 text-faint">{sub.location}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p>
        We will tell you by email before adding a subprocessor that receives customer content. We may
        also disclose data where the law compels it — and where we are permitted to tell you, we
        will.
      </p>

      <h2>6. Where it is processed</h2>
      <p>
        Our providers are established in the United States and process data there and at edge
        locations worldwide. Where personal data of people in the EEA or the UK is transferred, it is
        done under the European Commission's standard contractual clauses, which each of the
        providers above offers as part of its data processing agreement.
      </p>

      <h2>7. How long we keep it</h2>
      <ul>
        <li>
          <strong>While your account exists</strong> — your evidence, documents, questionnaires and
          answer history stay until you delete them or the account.
        </li>
        <li>
          <strong>On deletion</strong> — your content is removed from the live database immediately
          and from backups within 30 days.
        </li>
        <li>
          <strong>Invoices</strong> — kept by Polar for as long as tax law requires, typically seven
          years. We cannot delete these on request and neither can they.
        </li>
        <li>
          <strong>Logs</strong> — 30 days.
        </li>
      </ul>

      <h2>8. Your rights</h2>
      <p>
        You can ask for a copy of your data, correct it, delete it, restrict or object to how we use
        it, or ask for it in a portable format. Most of this you can do yourself from within the app;
        for anything else write to <Mail address={PRIVACY_EMAIL} /> and we will answer within 30 days.
      </p>
      <p>
        If you are in the EEA or the UK and think we have handled your data badly, you can complain
        to your data protection authority. We would rather you told us first.
      </p>

      <h2>9. Cookies</h2>
      <p>
        One cookie, and it holds your session. It is set when you sign in, it is required for the app
        to work, and it is removed when you sign out. There is no analytics cookie and no consent
        banner, because there is nothing to consent to.
      </p>

      <h2>10. Security</h2>
      <p>
        Passwords are hashed with PBKDF2 and never stored in a recoverable form. Session tokens are
        stored hashed, so signing out actually revokes them. Every query is scoped to a single
        account. Documents on your trust page sit behind a one-time, expiring access grant rather
        than a public URL.
      </p>
      <p>
        If you find a vulnerability, write to <Mail address={SECURITY_EMAIL} />. We will confirm
        receipt within two working days and will not pursue anyone who reports in good faith and
        gives us a reasonable chance to fix it.
      </p>

      <h2>11. Changes</h2>
      <p>
        Material changes are announced by email before they take effect, and the date at the top of
        this page is always the version in force.
      </p>
    </LegalPage>
  );
}

/* -------------------------------------------------------------------------- */
/* refunds                                                                    */
/* -------------------------------------------------------------------------- */

export function RefundsView({ signedIn }: { signedIn?: boolean }) {
  return (
    <LegalPage
      signedIn={signedIn}
      title="Refunds and cancellation"
      summary="Cancel whenever you like and keep what you paid for until it runs out. If you paid and it was not what you needed, tell us and we will send it back."
    >
      <h2>Cancelling</h2>
      <p>
        You can cancel from Settings at any time. There is no notice period, no retention flow and
        nobody to call.
      </p>
      <p>
        Cancelling stops the next charge. It does <strong>not</strong> cut you off on the spot: your
        plan keeps running to the end of the period you have already paid for, and the account drops
        to Free the day after. Somebody who cancels on day two of a month keeps all thirty days,
        because charging for a month and delivering two days of it would be a straightforward way to
        take somebody's money.
      </p>
      <p>
        Nothing is deleted when a plan ends. Your evidence, documents and answer history stay where
        they are; the Free plan's limits apply to what you can add from then on.
      </p>

      <h2>Refunds</h2>

      <h3>Your first payment: 14 days, no questions</h3>
      <p>
        If you are within 14 days of your first payment on a paid plan, write to{' '}
        <Mail address={SUPPORT_EMAIL} /> and we refund it in full. You do not have to explain, and we
        will not ask you to sit through a call first.
      </p>

      <h3>A renewal you did not mean to pay for</h3>
      <p>
        Renewals are announced by email before they are charged, but people are busy and a renewal
        can still arrive at a bad moment. If you contact us within 7 days of a renewal charge and you
        have not used the plan in the new period — no questionnaire imported, no answer drafted — we
        refund that renewal in full and cancel the subscription.
      </p>
      <p>
        Past that, or if the period has been used, the renewal is not automatically refundable. Ask
        anyway: if something went wrong at our end, we would rather fix it than win the argument.
      </p>

      <h3>If we get something wrong</h3>
      <p>
        Billed twice, charged after cancelling, or charged for a plan the service failed to deliver?
        That is a full refund whenever you notice it, and we do not apply a time limit to our own
        mistakes.
      </p>

      <h3>What is not refunded</h3>
      <ul>
        <li>
          Periods you have already used, beyond the windows above. The plan was available and the
          work it does is not undone by cancelling.
        </li>
        <li>
          Charges from your own model provider. Drafting runs on your API key, billed to you by them,
          and no part of it reaches us — which is also why we do not mark it up.
        </li>
        <li>
          Accounts terminated for breaking <a {...link('/terms')}>the terms</a>, such as using the
          service to attack it or to speak for a company you have no authority over.
        </li>
      </ul>

      <h2>How to ask</h2>
      <p>
        One email to <Mail address={SUPPORT_EMAIL} /> from the address on the account. Include the
        date of the charge if you have it, but we can find it without. We reply within two working
        days and, when the answer is yes, we submit the refund the same day.
      </p>

      <h2>How the money comes back</h2>
      <p>
        Refunds are issued through {MERCHANT_OF_RECORD.name}, our {MERCHANT_OF_RECORD.role}, to the
        original payment method. Their processing usually takes 5–10 working days to show on a card
        statement, which is a bank timeline rather than one either of us controls. You will get a
        credit note from Polar by email when it goes out.
      </p>

      <h2>Failed payments</h2>
      <p>
        If a renewal fails, Polar retries the card over several days and your plan keeps working
        during that time. Locking you out on the first decline would be punishing a customer for an
        expired card. If every retry fails, the subscription ends and the account moves to Free —
        again, without deleting anything.
      </p>

      <h2>Your statutory rights</h2>
      <p>
        Nothing on this page limits rights you have under consumer law where you live, including any
        statutory right to withdraw from a distance contract. Where those rights give you more than
        this policy does, they win.
      </p>
    </LegalPage>
  );
}

/* -------------------------------------------------------------------------- */
/* contact                                                                    */
/* -------------------------------------------------------------------------- */

export function ContactView({ signedIn }: { signedIn?: boolean }) {
  return (
    <LegalPage
      signedIn={signedIn}
      title="Contact us"
      summary="Three addresses, each read by a person. No ticket portal, no chatbot, and no form that files your question somewhere nobody looks."
    >
      <h2>Support</h2>
      <p>
        <Mail address={SUPPORT_EMAIL} /> — anything about your account, a plan, a charge, a refund,
        or a draft that behaved unexpectedly.
      </p>
      <p>
        <strong>We answer within two working days</strong>, and usually much faster. That is a
        commitment rather than an aspiration: if you have not heard back in two working days, send it
        again and say it is a second attempt.
      </p>
      <p>
        It helps to include the email address on the account and, for anything involving a
        questionnaire, the name of the customer it came from. Please do not paste evidence documents
        or your model API key into an email — we will never ask for either.
      </p>

      <h2>Security</h2>
      <p>
        <Mail address={SECURITY_EMAIL} /> — vulnerabilities in {COMPANY.product} itself, or a
        suspicion that an account has been accessed by somebody else.
      </p>
      <p>
        We confirm receipt within two working days. Good-faith research is welcome and we will not
        pursue anyone who reports responsibly and gives us a reasonable window to fix the issue
        before publishing. Please do not test against another company's trust page or account.
      </p>

      <h2>Privacy</h2>
      <p>
        <Mail address={PRIVACY_EMAIL} /> — access, export, correction or deletion requests, and
        anything else in <a {...link('/privacy')}>the privacy policy</a>. We answer within 30 days
        and normally within a few.
      </p>

      <h2>Invoices and payment details</h2>
      <p>
        Invoices, receipts, VAT identifiers and card changes are handled by{' '}
        {MERCHANT_OF_RECORD.name}, our {MERCHANT_OF_RECORD.role}. The fastest route is the customer
        portal, linked from Settings once you are on a paid plan — it has every invoice on the
        account and can reissue one with your tax details on it. Write to{' '}
        <Mail address={SUPPORT_EMAIL} /> if you cannot get in and we will sort it out.
      </p>

      {COMPANY.address ? (
        <>
          <h2>Postal address</h2>
          <p>
            {sellerName()}
            {COMPANY.address.map((line) => (
              <span key={line}>
                <br />
                {line}
              </span>
            ))}
          </p>
        </>
      ) : null}
    </LegalPage>
  );
}
